/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-11 22:41:00
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout, Card, TextStyle, Pagination } from '@shopify/polaris';
import { reduxForm, FieldArray, reset, getFormValues, change } from 'redux-form';
import { withI18n } from '@shopify/react-i18n';

import { routes } from 'src/routes';
import { NUMBER_SYNONYMS_PER_PAGE } from 'src/constants/synonym';
import { isEmptySynonyms, checkValidateSynonyms, buildSynonyms } from 'src/helpers/synonym-helpers';

// import DidYouKnow from 'src/components/views/synonym/did-you-know';
import InputError from 'src/components/views/synonym/input-error';
import SynonymsOnEdit from 'src/components/views/synonym/synonyms-on-edit';
import ActionsGroupOnUpdating from 'src/components/views/synonym/actions-group-on-updating';
import ConfirmRemoveModal from 'src/components/views/synonym/confirm-remove-modal';
import ProcessingUpdateBanner from 'src/components/views/common/processing-update-banner';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import TypeFormEmbedded from 'src/components/views/common/typeform-embedded';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';

import { confirmLeavePage } from 'src/helpers';
import * as synonymActions from 'src/stores/actions/synonym-actions';

import type { ComposedProps, State } from './edit.type';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';
import { StandardSearchTab } from 'src/constants/search'
;
import history from 'src/routes/history';

class EditSynonym extends Component<ComposedProps, State> {
  state = {
    // Search string
    inputSearchText: '',

    // Show/Hide remove modal
    activeRemoveModal: false,

    // Show/Hide deleting buttons
    activeDeletingButtons: false,

    skip: 0,
    limit: NUMBER_SYNONYMS_PER_PAGE,
    hasPrevious: false,
    hasNext: false,

    // List of selected synonyms to be deleted
    selectedSynonyms: [],
    discardDeletingChange: false,

    // Banner error
    dismissedBanner: true,
    messageError: '',
    deletedSynonyms: [],
    updatedSynonyms: {}
  };

  componentDidMount() {
    const { getSynonyms } = this.props;
    // Get list of synonym for selected page
    const queries = new URLSearchParams(window.location.hash);
    let q = queries.get('q') || '';
    let skip = queries.get('skip') || 0;
    let limit = queries.get('limit') || NUMBER_SYNONYMS_PER_PAGE;
    skip = parseInt(skip);
    limit = parseInt(limit);

    this.setState({ skip, limit, inputSearchText: q });
    getSynonyms(q, skip, limit);

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillReceiveProps(nextProps) {
    const { skip, limit } = this.state;
    const { total, deleting, saving, scheduleSyncIn, waitSyncAt } = nextProps;
    const {
      deleting: deletingPrev,
      saving: savingPrev,
      getSyncProcessStatus,
      updateShowBanner
    } = this.props;

    const hasNext = skip + limit < total;
    const hasPrevious = skip > 0;

    let updatedState = { hasNext, hasPrevious };

    // Push to management page if there's no data
    if (total === 0) {
      this.backToManagementPage();
    }

    // Hide confirm remove modal
    if (deleting === false && deletingPrev === true) {
      updatedState = _.assign(updatedState, {
        activeRemoveModal: false,
        activeDeletingButtons: false,
        selectedSynonyms: []
      });
    }

    this.setState(updatedState);

    // Check to show banner Processing update when change status/ import
    if ((savingPrev && !saving) || (deletingPrev && !deleting)) {
      if (waitSyncAt) {
        // Time from now to sync point
        let syncDuration = waitSyncAt - Math.floor(Date.now() / 1000);
        syncDuration = syncDuration > 0 ? syncDuration : 0;
        // Time to finish sync point
        syncDuration = Math.floor(syncDuration / 60) + scheduleSyncIn;

        updateShowBanner(syncDuration, true);
      }
      // Get process sync status
      getSyncProcessStatus();
    }
  }

  render() {
    const {
      i18n,
      dirty,
      handleSubmit,
      pristine,
      submitting,
      saving,
      deleting,
      formValues,
      syncDuration,
      showScheduleSync,
      updateSuccess,
      selectedPlan
    } = this.props;
    const {
      skip,
      limit,
      discardDeletingChange,
      dismissedBanner,
      messageError,
      hasPrevious,
      hasNext,
      inputSearchText,
      updatedSynonyms,
      deletedSynonyms
    } = this.state;

    const title = !_.isEmpty(inputSearchText) ? (
      <TextStyle variation="strong">
        {i18n.translate('AdminHTML.Synonym.Edit.synonymsContaining', {
          nt_text_search: <i>{inputSearchText}</i>
        })}
      </TextStyle>
    ) : (
      ''
    );
    const emptySynonyms = formValues ? isEmptySynonyms(formValues['synonyms']) : true;
    const isUpdated = !_.isEmpty(updatedSynonyms) || !_.isEmpty(deletedSynonyms);
    const disabledSave = isUpdated ? false : (pristine || submitting || emptySynonyms);
    return (
      <div className="BC-Page BC-Page__Synonym">
        <Page
          title={i18n.translate('AdminHTML.Synonym.Edit.title')}
          breadcrumbs={[
            {
              content: i18n.translate('AdminHTML.Synonym.Synonym.title'),
              onAction: this.backToManagementPage
            }
          ]}
          primaryAction={
            <div className="BC-Page BC-Page__Button BC-Page__Button-HasBreadcrumb">
              <ActionsGroupOnUpdating
                handleSubmit={handleSubmit(this.handleSave.bind(this))}
                handleCancel={this.backToManagementPage}
                pristine={isUpdated ? false : pristine}
                disabled={disabledSave}
                saving={saving}
                emptySynonyms={emptySynonyms}
              />
            </div>
          }>
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Did you know? */}
                {/* <DidYouKnow /> */}

                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />

                {/* Processing update banner */}
                {showScheduleSync ? (
                  <ProcessingUpdateBanner syncDuration={syncDuration} featureName="synonyms" />
                ) : (
                  ''
                )}
                {/* Banner error */}
                {!dismissedBanner && (
                  <InputError onDismiss={this.handleDismissBanner} message={messageError} />
                )}
              </div>
            </Layout.Section>

            <Layout.Section>
              <Card title={title}>
                {/* List of synonym*/}
                <form onSubmit={this.handleDefaultSubmit} className="editSynonyms">
                  <FieldArray
                    name="synonyms"
                    component={SynonymsOnEdit}
                    skip={skip}
                    limit={limit}
                    deletedSynonyms={deletedSynonyms}
                    updatedSynonyms={updatedSynonyms}
                    addDeletedSynonyms={(id) => this.setState({
                      deletedSynonyms: [...deletedSynonyms, id]
                    })}
                    addUpdatedSynonyms={(synonym) => this.setState({
                      updatedSynonyms: {...updatedSynonyms, [synonym.id]: synonym}
                    })}
                    handleChangeDeletingButtonGroup={this.handleChangeDeletingButtonGroup}
                    discardDeletingChange={discardDeletingChange}
                    turnOffDiscardDeletingChangeFlag={this.turnOffDiscardDeletingChangeFlag}
                  />
                </form>
              </Card>

              {/* Pagination*/}
              <div className="pagination">
                {(hasPrevious || hasNext) && (
                  <Pagination
                    hasPrevious={hasPrevious}
                    onPrevious={this.getPreviousPage}
                    hasNext={hasNext}
                    onNext={this.getNextPage}
                  />
                )}

                <div className="actionsGroup">
                  <ActionsGroupOnUpdating
                    handleSubmit={handleSubmit(this.handleSave.bind(this))}
                    handleCancel={this.backToManagementPage}
                    pristine={isUpdated ? false : pristine}
                    disabled={disabledSave}
                    saving={saving}
                    emptySynonyms={emptySynonyms}
                  />
                </div>
              </div>
            </Layout.Section>
          </Layout>
        </Page>

        <ConfirmRemoveModal
          active={this.state.activeRemoveModal}
          handleChange={this.handleChangeModal}
          handleRemove={this.deleteSynonyms}
          title={i18n.translate('AdminHTML.Synonym.Edit.deleteSelected')}
          content={i18n.translate('AdminHTML.Synonym.Edit.confirmDelete')}
          deleting={deleting}
        />

        {!_.isEmpty(selectedPlan) && (
          <TypeFormEmbedded type="synonym" conditionShow={updateSuccess} />
        )}

        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty || isUpdated}
          // Navigate function
          navigate={path => history.push({
            pathname: path,
            hash: StandardSearchTab.manualSynonyms
          })}
          shouldBlockNavigation={dirty || isUpdated}
        />
      </div>
    );
  }

  // Back to management synonyms
  backToManagementPage = () => {
    history.push(routes.synonym.view());
  };

  // Go to previous page
  getPreviousPage = () => {
    const { skip, limit, inputSearchText } = this.state;
    const previousSkip = skip - limit;
    const previousLimit = NUMBER_SYNONYMS_PER_PAGE;

    // Prevent multiple click that the skip number is less than 0
    if (previousSkip < 0) return;

    // Set skip, limit for next page
    this.setState({ skip: previousSkip, limit: previousLimit });
    this.props.getSynonyms(inputSearchText || '', previousSkip, previousLimit);
  };

  // Go to next page
  getNextPage = () => {
    const { skip, limit, inputSearchText } = this.state;
    const { total } = this.props;
    const nextSkip = skip + limit;

    // Prevent multiple click that the skip number is bigger than total
    if (nextSkip >= total) return;

    // Set skip, limit for next page and disable flag for first page loading, don't need to display skeleton loading
    this.setState({ skip: nextSkip, limit });
    this.props.getSynonyms(inputSearchText || '', nextSkip, NUMBER_SYNONYMS_PER_PAGE);
  };

  // Handle show/hide deleting button
  handleChangeDeletingButtonGroup = (status, selectedSynonyms) => {
    this.setState({ activeDeletingButtons: status, selectedSynonyms });
  };

  // Handle open/close modal delete
  handleChangeModal = () => {
    this.setState(({ activeRemoveModal }) => ({
      activeRemoveModal: !activeRemoveModal
    }));
  };

  // Save synonyms
  handleDefaultSubmit = e => {
    e.preventDefault();
  };

  // Save synonyms
  async handleSave() {
    const { i18n, updateSynonyms, deleteSynonyms } = this.props;
    const { updatedSynonyms, deletedSynonyms } = this.state;
    const synonyms = Object.values(updatedSynonyms);

    const validateSynonymsResult = checkValidateSynonyms(synonyms);

    if (!_.isEmpty(validateSynonymsResult)) {
      this.setState({
        dismissedBanner: false,
        messageError: i18n.translate(validateSynonymsResult)
      });
      return;
    }

    this.setState({ dismissedBanner: true, messageError: '' });

    if (deletedSynonyms.length > 0) {
      await deleteSynonyms(deletedSynonyms);
    }

    // Save updated synonyms
    // Build synonyms data before saving
    const synonymsToUpdated = buildSynonyms(synonyms).filter(s => !deletedSynonyms.includes(s.id));

    await updateSynonyms(synonymsToUpdated);

    Emitter.emit(EVENT_KEY.RELOAD_CSAT_ADMIN_APP_DISPLAY);
    this.setState({ updatedSynonyms: {}, deletedSynonyms: [] })
    this.backToManagementPage();
  }

  // Discard
  discardChange = () => {
    const { resetForm } = this.props;

    // Reset form
    resetForm();
  };

  // Delete selected synonym
  discardDeletingChange = () => {
    this.setState({
      activeDeletingButtons: false,
      selectedSynonyms: [],
      discardDeletingChange: true
    });
  };

  turnOffDiscardDeletingChangeFlag = () => {
    this.setState({ discardDeletingChange: false });
  };

  deleteSynonyms = async () => {
    const { selectedSynonyms } = this.state;
    const { synonyms } = this.props;

    // Collect synonym ids
    const synonymIds = _.map(selectedSynonyms, item => {
      const { index } = item;
      const synonym = synonyms[index] || {};
      return synonym.id;
    });

    // Delete synonyms

    return synonymIds;
  };

  // Handle hide banner
  handleDismissBanner = () => {
    // Hide banner
    this.setState({ dismissedBanner: true, messageError: '' });
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { dirty, i18n } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getSynonyms: (q, skip, limit) =>
    dispatch(synonymActions.getSynonyms({ q, skip, limit }, ownProps.i18n)),
  updateSynonyms: synonyms =>
    dispatch(synonymActions.updateSynonyms(synonyms, undefined, ownProps.i18n)),
  deleteSynonyms: termIds => dispatch(synonymActions.deleteSynonyms(termIds, ownProps.i18n)),
  resetForm: () => dispatch(reset('EditSynonymsForm')),
  updateField: (field, value) => dispatch(change(`EditSynonymsForm`, field, value)),
});

const mapStateToProps = (state, props) => {
  const synonyms = _.get(state, 'synonym.synonyms', []);
  const formValues = getFormValues('EditSynonymsForm')(state);

  return {
    list: _.get(state.synonym, 'synonyms', []),
    total: _.get(state.synonym, 'total', 0),
    loading: _.get(state.synonym, 'loading', false),
    saving: _.get(state.synonym, 'saving', false),
    deleting: _.get(state.synonym, 'deleting', false),
    updateSuccess: _.get(state.synonym, 'updateSuccess', false),
    synonyms,
    initialValues: { synonyms },
    formValues,
    scheduleSyncIn: _.get(state.synonym, 'scheduleSyncIn', 0),
    waitSyncAt: _.get(state.synonym, 'waitSyncAt', 0),
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan')
  };
};

const EditSynonymsForm = reduxForm({
  form: 'EditSynonymsForm', // a unique identifier for this form
  enableReinitialize: true
})(EditSynonym);

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(EditSynonymsForm)),
  'SyncSynonym'
);
